const PUBLIC_DRIVER_DIR = "/drivers";

const DRIVER_IMAGE_OVERRIDES = {
    albon: "alexanderalbon",
    alexanderalbon: "alexanderalbon",
    arvid_lindblad: "arvidlindblad",
    arvidlindblad: "arvidlindblad",
    antonelli: "kimiantonelli",
    kimiantonelli: "kimiantonelli",
    bearman: "oliverbearman",
    oliverbearman: "oliverbearman",
    bottas: "valtteribottas",
    valtteribottas: "valtteribottas",
    bortoleto: "gabrielbortoleto",
    gabrielbortoleto: "gabrielbortoleto",
    colapinto: "francocolapinto",
    francocolapinto: "francocolapinto",
    gasly: "pierregasly",
    pierregasly: "pierregasly",
    hulkenberg: "nicohulkenberg",
    nicohulkenberg: "nicohulkenberg",
    lawson: "liamlawson",
    liamlawson: "liamlawson",
    max_verstappen: "maxverstappen",
    maxverstappen: "maxverstappen",
    perez: "sergioperez",
    sergioperez: "sergioperez",
    sainz: "carlossainz",
    carlossainz: "carlossainz",
    stroll: "lancestroll",
    lancestroll: "lancestroll",
};

const LOCAL_IMAGE_KEYS = new Set([
    "alexanderalbon",
    "arvidlindblad",
    "carlossainz",
    "charlesleclerc",
    "estebanocon",
    "fernandoalonso",
    "francocolapinto",
    "gabrielbortoleto",
    "georgerussell",
    "isackhadjar",
    "kimiantonelli",
    "lancestroll",
    "landonorris",
    "lewishamilton",
    "liamlawson",
    "maxverstappen",
    "nicohulkenberg",
    "oliverbearman",
    "oscarpiastri",
    "pierregasly",
    "sergioperez",
    "valtteribottas",
]);

export function normalizeDriverAssetKey(value = "") {
    return String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toLowerCase();
}

export function getDriverImagePath(driver) {
    if (driver?.image_url) return driver.image_url;
    const idKey = normalizeDriverAssetKey(driver?.id || driver?.driver_id || "");
    const nameKey = normalizeDriverAssetKey(
        driver?.full_name ||
            driver?.display_name ||
            `${driver?.first_name || ""}${driver?.last_name || ""}`
    );
    const key = DRIVER_IMAGE_OVERRIDES[idKey] || DRIVER_IMAGE_OVERRIDES[nameKey] || nameKey;
    return LOCAL_IMAGE_KEYS.has(key) ? `${PUBLIC_DRIVER_DIR}/${key}.png` : null;
}

export function buildDriverEnrichment(driver) {
    const teamName = driver.teamName || "their team";
    const rankText = driver.rank ? `P${driver.rank}` : "the championship table";
    const isLegend = driver.status === "legend";
    const hasBackendBio =
        driver.bio &&
        !String(driver.bio).toLowerCase().includes("biography enrichment is not connected yet");

    const summary = hasBackendBio
        ? driver.bio
        : isLegend
            ? `${driver.name} is part of Outlapped's historical driver library. Admin-managed biography notes can be added here later without changing this page.`
            : `${driver.name} is currently listed with ${teamName} and sits ${rankText}. This profile is designed to combine admin-managed facts with live driver performance once richer data is connected.`;

    const recentAverage = driver.recentForm.length
        ? driver.recentForm.reduce((total, value) => total + Number(value || 0), 0) / driver.recentForm.length
        : null;

    const strengths = driver.strengths.length
        ? driver.strengths
        : [
              driver.championships > 0 ? "Championship experience" : "Race execution",
              driver.wins > 0 || driver.careerWins > 0 ? "Front-running pace" : "Points-scoring opportunities",
              recentAverage && recentAverage <= 8 ? "Recent form" : "Long-run development",
          ];

    const weaknesses = [
        "Detailed weakness scoring is not connected yet.",
        "Use admin notes or future telemetry models to replace this placeholder.",
    ];

    const analysis = [
        {
            label: "Recent form",
            body: driver.recentForm.length
                ? `${driver.name}'s last ${driver.recentForm.length} recorded finishes average P${recentAverage.toFixed(1)}.`
                : "Recent race-form data is not available for this driver yet.",
        },
        {
            label: "Qualifying performance",
            body: driver.qualifyingRank
                ? `Current qualifying rank is P${driver.qualifyingRank}.`
                : "Qualifying rank is not exposed by the current API yet.",
        },
        {
            label: "Race pace",
            body: driver.racePaceRank
                ? `Current race pace rank is P${driver.racePaceRank}.`
                : "Race pace rank can be connected later without changing the modal layout.",
        },
        {
            label: "Consistency",
            body: driver.consistency
                ? `${driver.consistency}% consistency score based on available UI data.`
                : "Consistency scoring needs richer historical inputs before it can be shown confidently.",
        },
    ];

    return {
        summary,
        strengths,
        weaknesses,
        analysis,
    };
}
